/**
 * ScrollerPanels Web Component
 * Handles smooth crossfade transitions between media panels based on text panel visibility
 */
class ScrollerPanels extends HTMLElement {
  constructor() {
    super();
    this.textPanels = [];
    this.mediaPanels = [];
    this.observer = null;
    this.currentActivePanel = null;
  }

  connectedCallback() {
    this.reorganizeBlocks();
    this.initializePanels();
    this.setupIntersectionObserver();
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  /**
   * Reorganizes blocks into their respective containers and sets up data-panel attributes
   */
  reorganizeBlocks() {
    const leftContainer = this.querySelector('.scroller__panels-left');
    const rightContainer = this.querySelector('.scroller__panels-right');
    
    if (!leftContainer || !rightContainer) {
      console.warn('ScrollerPanels: Required containers not found');
      return;
    }

    // Process each shopify block
    const shopifyBlocks = Array.from(this.querySelectorAll('.shopify-block'));
    shopifyBlocks.forEach(block => {
      const blockId = block.id;
      const mediaPanel = block.querySelector('.scroller__panel-media');
      
      // Link media panel to its block via data-panel attribute
      if (mediaPanel && blockId) {
        mediaPanel.setAttribute('data-panel', blockId);
      }
      
      leftContainer.appendChild(block);
    });

    // Position media panels for crossfade effect (clone into right container, keep originals in place for mobile)
    const mediaPanels = Array.from(this.querySelectorAll('.scroller__panel-media'));
    mediaPanels.forEach((panel, index) => {
      const clone = panel.cloneNode(true);
      this.setupMediaPanelPositioning(clone, index);
      rightContainer.appendChild(clone);
    });
  }

  /**
   * Sets up absolute positioning for media panels to enable crossfade effect
   */
  setupMediaPanelPositioning(panel, index) {
    panel.style.position = 'absolute';
    panel.style.top = '0';
    panel.style.left = '0';
    panel.style.width = '100%';
    panel.style.height = '100%';
    panel.style.zIndex = index === 0 ? '1' : '0';
  }

  /**
   * Initializes text and media panels and sets up initial visibility states
   */
  initializePanels() {
    const leftContainer = this.querySelector('.scroller__panels-left');
    const rightContainer = this.querySelector('.scroller__panels-right');
    
    this.textPanels = leftContainer ? Array.from(leftContainer.querySelectorAll('.scroller__panel-text')) : [];
    this.mediaPanels = rightContainer ? Array.from(rightContainer.querySelectorAll('[data-panel]')) : [];
    
    this.setupInitialPanelStates();
  }

  /**
   * Sets up initial visibility states for all media panels
   */
  setupInitialPanelStates() {
    this.mediaPanels.forEach((panel, index) => {
      panel.style.transition = 'opacity 0.5s ease-in-out';
      
      if (index === 0) {
        panel.style.opacity = '1';
        panel.style.visibility = 'visible';
        this.currentActivePanel = panel.getAttribute('data-panel');
      } else {
        panel.style.opacity = '0';
        panel.style.visibility = 'hidden';
      }
    });
  }

  /**
   * Sets up intersection observer to detect when text panels come into view
   */
  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '-20% 0px -20% 0px',
      threshold: 0.5
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.handlePanelInView(entry.target);
        }
      });
    }, options);

    this.textPanels.forEach(panel => {
      this.observer.observe(panel);
    });
  }

  /**
   * Handles when a text panel comes into view
   */
  handlePanelInView(textPanel) {
    const parentBlock = textPanel.closest('.shopify-block');
    const panelId = parentBlock ? parentBlock.id : textPanel.id;
    
    const correspondingMediaPanel = this.mediaPanels.find(panel => 
      panel.getAttribute('data-panel') === panelId
    );

    if (correspondingMediaPanel && this.currentActivePanel !== panelId) {
      this.transitionToPanel(correspondingMediaPanel, panelId);
    }
  }

  /**
   * Initiates transition to a new media panel
   */
  transitionToPanel(newPanel, panelId) {
    if (this.currentActivePanel) {
      const currentMediaPanel = this.mediaPanels.find(panel => 
        panel.getAttribute('data-panel') === this.currentActivePanel
      );
      
      if (currentMediaPanel) {
        this.crossfadePanels(currentMediaPanel, newPanel, panelId);
      } else {
        this.showPanel(newPanel);
        this.currentActivePanel = panelId;
      }
    } else {
      this.showPanel(newPanel);
      this.currentActivePanel = panelId;
    }
  }

  /**
   * Creates smooth crossfade effect between current and new media panels
   */
  crossfadePanels(currentPanel, newPanel, panelId) {
    // Set up layering for crossfade
    newPanel.style.zIndex = '2';
    currentPanel.style.zIndex = '1';
    
    // Prepare new panel for fade in
    newPanel.style.visibility = 'visible';
    newPanel.style.opacity = '0';
    newPanel.style.transition = 'opacity 0.5s ease-in-out';
    
    // Start fading out current panel
    currentPanel.style.transition = 'opacity 0.5s ease-in-out';
    currentPanel.style.opacity = '0';
    
    // Start crossfade effect
    requestAnimationFrame(() => {
      newPanel.style.opacity = '1';
    });
    
    this.currentActivePanel = panelId;
    
    // Clean up old panel after transition
    setTimeout(() => {
      if (currentPanel.style.opacity === '0') {
        currentPanel.style.visibility = 'hidden';
        currentPanel.style.zIndex = '0';
      }
    }, 500);
  }

  /**
   * Shows a media panel with fade in effect
   */
  showPanel(panel) {
    panel.style.visibility = 'visible';
    panel.style.transition = 'opacity 0.5s ease-in-out';
    
    requestAnimationFrame(() => {
      panel.style.opacity = '1';
    });
  }

  /**
   * Hides a media panel with fade out effect
   */
  hidePanel(panel) {
    panel.style.transition = 'opacity 0.5s ease-in-out';
    panel.style.opacity = '0';
    
    setTimeout(() => {
      if (panel.style.opacity === '0') {
        panel.style.visibility = 'hidden';
      }
    }, 500);
  }
}

// Register the custom element
customElements.define('scroller-panels', ScrollerPanels);
